import { useEffect, useMemo, useState } from 'react';
import { useRxCollection } from 'rxdb/plugins/react';
import type { NavigateFunction } from 'react-router-dom';
import { useAuth } from '../features/auth/useAuth';
import { useAbility } from '../features/auth/abilityContext';
import { useRolesSettled } from '../features/auth/useRolesSettled';
import {
    allowedFieldCategories,
    fieldCategoryPath,
    fieldCategoryFromKey
} from '../features/field/fieldCategories';
import type { AppArea } from '../utils/lastApp';
import {
    parseRestorePath,
    rememberLocation,
    rememberedLocation
} from '../utils/lastLocation';

/**
 * Bound on the local-database queries below, which settle in milliseconds —
 * only so an answer that never comes cannot leave somebody on a spinner. (The
 * wait for the user's ROLES has its own bound — see useRolesSettled.)
 */
const RESTORE_WAIT_MS = 8000;

/**
 * Whether the document `id` is in `collectionName`: null until the local
 * database has answered for THIS id.
 *
 * rxdb's useRxDocument cannot say that — it reports "not loading, no document"
 * before its query has even started, which reads exactly like "not on this
 * device".
 */
function useStoredPresence(
    collectionName: string,
    id: string | null
): boolean | null {
    const collection = useRxCollection<{ id: string }>(collectionName);
    const [answer, setAnswer] = useState<{
        id: string;
        present: boolean;
    } | null>(null);

    useEffect(() => {
        if (!collection || id === null) {
            return;
        }
        const subscription = collection
            .findOne(id)
            .$.subscribe((doc) => setAnswer({ id, present: doc !== null }));
        return () => subscription.unsubscribe();
    }, [collection, id]);

    if (id === null || answer?.id !== id) {
        return null;
    }
    return answer.present;
}

/**
 * Go to a restored path the way the user would have got there themselves.
 *
 * A record editor is a child ROUTE of its list, and closing it steps BACK (see
 * useDialogClose) — so the list has to be underneath it in the history, or the
 * close button would step out of the app instead.
 */
export function navigateToRestored(
    navigate: NavigateFunction,
    path: string
): void {
    const target = parseRestorePath(path);
    if (!target.record) {
        navigate(path, { replace: true });
        return;
    }
    navigate(target.listPath, { replace: true });
    navigate(path);
}

/**
 * Where opening the app should land the user: back where they left off, or the
 * app's own landing page when that spot no longer holds.
 *
 * The restore is refused when the Maßnahme is not on this device at all. A
 * record that is gone costs only the editor and lands on its list; a category
 * the user may no longer record in lands on Befunde. It waits for the roles to
 * replicate before judging any of that — until they land everyone looks
 * role-less.
 *
 * Every doubt is settled in FAVOUR of the remembered spot, and none of them ever
 * erases it: from here a deleted Maßnahme looks exactly like one that has not
 * replicated back yet, and each resolves itself on a later start. Refusing hard
 * would turn a slow cold start — what a phone hands us — into a permanent one.
 *
 * Office rights are NOT re-checked here. Every office screen guards itself
 * (RequireAbility) and the office door bounces field-only crew (RequireOffice),
 * so a restored path the user may no longer open lands on the office home by the
 * same route a bookmark would — one table of rights, not two.
 *
 * Returns null while it is still deciding. With nothing remembered it decides at
 * mount, so a first launch never waits. Pass a null `area` to sit this one out
 * (the user asked for the project picker: see fieldPickerPath).
 */
export function useRestoreLocation(area: AppArea | null): string | null {
    const { user } = useAuth();
    const ability = useAbility();
    const settled = useRolesSettled();

    // Safe to re-read: `/` and the two landing pages are never written (see
    // restorablePath), so nothing can move under us while we are deciding.
    const remembered = useMemo(
        () => (area && user ? rememberedLocation(user.id, area) : null),
        [area, user]
    );
    const target = useMemo(
        () => (remembered ? parseRestorePath(remembered) : null),
        [remembered]
    );

    const projectId = target?.projectId ?? null;
    const projectStored = useStoredPresence('projects', projectId);

    // One query for whichever collection the open editor belongs to. Without a
    // record it stands idle on a collection that always exists.
    const record = target?.record ?? null;
    const recordStored = useStoredPresence(
        record?.collection ?? 'projects',
        record?.id ?? null
    );

    /**
     * Decided once and never revisited: re-deciding on a later render would
     * fight the navigation the caller has already made on our answer.
     *
     * A null `area` has no answer at all, and neither has a user we do not know
     * yet: the memory is kept PER PERSON, so deciding before anybody is signed
     * in would find nothing and settle on the landing page for good. Not a rare
     * state — an expired session renders the app with the re-login dialog over
     * it and nobody signed in.
     */
    const [resolved, setResolved] = useState<string | null>(() => {
        if (!area || !user) {
            return null;
        }
        return rememberedLocation(user.id, area) ? null : `/${area}`;
    });
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setTimedOut(true), RESTORE_WAIT_MS);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!area || resolved) {
            return;
        }
        const home = `/${area}`;
        if (!user) {
            // Nobody signed in — whoever answers the re-login dialog decides
            // whose spot this is. Only give up if that answer never comes.
            if (timedOut) {
                // The effect IS the subscription to the roles, the local DB and
                // a timeout, not derived state.
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setResolved(home);
            }
            return;
        }
        if (!target) {
            setResolved(home);
            return;
        }

        // The checks below are worth nothing until what they read has arrived —
        // the roles for access, the local database for existence.
        const pending =
            !settled ||
            (projectId !== null && projectStored === null) ||
            (record !== null && recordStored === null);
        if (pending) {
            if (!timedOut) {
                return;
            }
            /**
             * Out of patience. An unanswered check is not a failed one: go back
             * where the user was and let the page cope with whatever is
             * missing, exactly as it would for a bookmark. A cold start that
             * takes its time is the NORMAL state of a phone.
             */
            setResolved(target.path);
            return;
        }

        if (projectId && !projectStored) {
            /**
             * Not on this device: deleted, or not replicated back yet after the
             * browser cleared the local copy. Those look alike from here, so the
             * landing page is for THIS launch only — the memory stays, and the
             * spot returns with the Maßnahme.
             */
            setResolved(home);
            return;
        }

        if (area === 'field' && projectId) {
            const allowed = allowedFieldCategories(ability, projectId);
            const category = fieldCategoryFromKey(target.category);
            /**
             * An ability that allows NOTHING here is not read as a refusal — it
             * is also what a half-assembled one looks like: the rights on a
             * Maßnahme come from the membership embedded on the project and
             * group docs, which replicate on their own schedule, so settled
             * roles say nothing about them.
             */
            if (
                allowed.length > 0 &&
                (!category || !allowed.some((one) => one.key === category))
            ) {
                // The Maßnahme is still ours, this part of it is not — land on
                // Befunde, the fallback everywhere else too (fieldCategoryPath).
                const fallback = fieldCategoryPath(ability, projectId, null);
                rememberLocation(user.id, fallback);
                setResolved(fallback);
                return;
            }
        }

        if (record && !recordStored) {
            // The record is gone; its list is still where the user was.
            rememberLocation(user.id, target.listPath);
            setResolved(target.listPath);
            return;
        }

        setResolved(target.path);
    }, [
        area,
        resolved,
        target,
        user,
        ability,
        settled,
        timedOut,
        projectId,
        projectStored,
        record,
        recordStored
    ]);

    return resolved;
}
